import React, {useState, useEffect, FC} from "react"
import {get} from "lodash"

import {UserTracking as tracking, UserFlowIds} from "@irati-tracking/onboarding"
import History from "@irati/utils/history"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {User, UserMetadata} from "@irati/types"
import {withTheme, WithDefaultTheme} from "styled-components"
import customisationService from "@irati/onboarding/services/customisation"
import {H2, Body} from "@irati/ui/src/Typography"
import Spacing from "@irati/ui/src/Spacing"

import {BannerWrapper, TextWrapper, ButtonWrapper, StyledButton, StyledPill, StyledClose} from "./styled"
import CloseBannerModal from "./CloseBannerModal"

interface Props extends InjectIntl, WithDefaultTheme {
  user: User,
  metadata: UserMetadata | null,
  suggestions: string[]
}

const CustomisationBanner: FC<Props> = ({user, metadata, suggestions}) => {
  const [showBanner, setShowBanner] = useState<boolean | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)

  useEffect(() => {
    const favouriteCategories = get(metadata || {}, "config.favouriteCategories")
    if (favouriteCategories && favouriteCategories.length > 0) {
      setShowBanner(false)
      return
    }
    const shouldShowBanner = () => {
      return customisationService.shouldShowCategoriesBanner(user).then((shouldShowCategoriesBanner) => {
        setShowBanner(shouldShowCategoriesBanner)
      })
    }
    shouldShowBanner()
  }, [])

  const onClickClose = () => {
    tracking.bannerDismissed({
      flow: UserFlowIds.CategoriesCustomisation
    })
    setShowModal(true)
  }

  const hideBanner = () => {
    tracking.modalButtonClicked({
      flow: UserFlowIds.CategoriesCustomisation
    })
    customisationService.dismissCategoriesBanner(user)
    setShowBanner(false)
    setShowModal(false)
  }

  const buttonClicked = () => {
    tracking.bannerButtonClicked({
      flow: UserFlowIds.CategoriesCustomisation
    })
    History.push("/account/settings/customisation/categories")
  }

  const linkClicked = () => {
    tracking.modalLinkClicked({
      flow: UserFlowIds.CategoriesCustomisation
    })
    History.push("/account/settings/customisation/categories")
  }

  if (!showBanner) return null

  return (
    <>
      <BannerWrapper>
        <TextWrapper>
          <H2 marginBottom={1}>
            <FormattedMessage id="perks.containers.homepage.customisation.customisationHeader" />
          </H2>
          <Body>
            <FormattedMessage id="perks.containers.homepage.customisation.description" />
          </Body>
          <Spacing marginTop={2} marginBottom={-1}>
            {suggestions.map((name) => {
              return <StyledPill key={name}>{name}</StyledPill>
            })}
          </Spacing>
        </TextWrapper>
        <ButtonWrapper>
          <StyledButton full primary onClick={buttonClicked}>
            <FormattedMessage id="perks.containers.homepage.customisation.buttonLabel" />
          </StyledButton>
        </ButtonWrapper>
        <StyledClose onClick={onClickClose} />
      </BannerWrapper>
      <CloseBannerModal onCloseModal={hideBanner} onLinkClicked={linkClicked} showModal={showModal} />
    </>
  )
}

export default injectIntl(withTheme(CustomisationBanner))
